import { AskRequest, AskRequestComment, User } from '@hone-automation/common';
import { format, isValid, parse, parseISO } from 'date-fns';

import { MessagesCardProps } from './MessagesCard';
import {
  messagePreviewDetectors,
  type EmailMetadata,
  type MessageDetailsContent,
} from './subcomponents/MessageContent/analyzeMessageContent';
import { AssignedTo, AssignedToType } from '../../types/Todos';
import { Attachment } from '../../../Queue/Chat/Chat';
import type { CheckListItemProps } from '../../Common/CheckList/CheckList';
import { formatAskRequestDetails } from '../../../Queue/utils/askRequestDetailsFormatter';

export interface QueueCommentAttachment {
  filename: string;
  attachmentId: string;
  fileType?: string;
}

export interface QueueCommentUserLocation {
  userId?: string;
  locationId?: string;
  locationName?: string;
  email?: string;
  role?: string;
  isHoneInternalUser?: boolean;
}

export type AskRequestQueueComment = AskRequestComment & {
  attachments?: QueueCommentAttachment[];
  userLocation?: QueueCommentUserLocation | null;
  taggedUserIds?: string[] | string | null;
};

export interface BuildMessagesCardOptions {
  /**
   * The currently authenticated user. Used to derive `self` and mention status.
   */
  currentUserId?: string;
  /**
   * Optional override to explicitly flag the message as sent via email.
   */
  forceSentEmail?: boolean;
  /**
   * Pass-through list of users available for mention formatting.
   */
  users?: User[];
  /**
   * Optional fallback user information if the author might be deleted.
   */
  fallbackUser?: {
    userId: string;
    name: string;
  };
  skipMentionFormatting?: boolean;
  attachments?: Attachment[];
  checklist?: CheckListItemProps[];
  details?: MessageDetailsContent;
  isFirstMessage?: boolean;
  emailMetadata?: EmailMetadata;
  request?: AskRequest;
  /**
   * Optional sectionAmount from links data for flagged cells.
   * Used to show Current Amount vs Original Amount.
   */
  sectionAmount?: number;
}

const parseTaggedUserIds = (tagged?: AskRequestQueueComment['taggedUserIds']): string[] => {
  if (!tagged) {
    return [];
  }

  if (Array.isArray(tagged)) {
    return tagged.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
  }

  if (typeof tagged === 'string') {
    try {
      const parsed = JSON.parse(tagged);
      if (Array.isArray(parsed)) {
        return parsed.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
      }
    } catch (error) {
      // Swallow JSON parse errors and fall through to empty array
    }
  }

  return [];
};

const parseDateValue = (value?: string | Date | null): Date | undefined => {
  if (!value) {
    return undefined;
  }

  if (value instanceof Date) {
    return value;
  }

  const normalizedString = (() => {
    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();

    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed)) {
      return trimmed.replace(' ', 'T') + 'Z';
    }

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(trimmed)) {
      return trimmed + 'Z';
    }

    return trimmed;
  })();

  // Attempt ISO parsing first (handles strings ending with Z / timezone info)
  const isoCandidate = typeof normalizedString === 'string' ? parseISO(normalizedString) : normalizedString;
  if (isValid(isoCandidate)) {
    return isoCandidate;
  }

  // Fallback to common "yyyy-MM-dd HH:mm:ss" format used by the queue API
  const fallback = typeof value === 'string' ? parse(value, 'yyyy-MM-dd HH:mm:ss', new Date()) : undefined;
  return isValid(fallback) ? fallback : undefined;
};

const deriveAssignedToType = (comment: AskRequestQueueComment): AssignedToType => {
  const role = comment.userLocation?.role?.toLowerCase();
  const name = comment.user?.name || comment.user?.username;
  const nameLower = name?.toLowerCase();

  // KAI detection (Kit or Hone Admin)
  if (nameLower === 'kit' || nameLower === 'hone admin') {
    return 'kai';
  }

  if (role?.includes('external')) {
    return 'ext-email';
  }

  if (role?.includes('client')) {
    return 'client';
  }

  if (comment.user?.isInternalUser || comment.userLocation?.isHoneInternalUser) {
    return 'ks';
  }

  return 'client';
};

const deriveUserTypeLabel = (role: string | undefined, assignedToType: AssignedToType): string | undefined => {
  if (role) {
    const normalized = role.toLowerCase();
    if (normalized.includes('bookkeeper')) {
      return 'Accountant';
    }
    if (normalized.includes('client')) {
      return 'Client';
    }
    if (normalized.includes('external')) {
      return 'External Email';
    }
  }

  switch (assignedToType) {
    case 'ks':
      return 'Accountant';
    case 'client':
      return 'Client';
    case 'ext-email':
      return 'External Email';
    case 'kai':
      return 'KAI';
    default:
      return undefined;
  }
};

const extractFileType = (filename?: string): string | undefined => {
  if (!filename?.includes('.')) {
    return undefined;
  }

  return filename.split('.').pop()?.toLowerCase();
};

const buildAssignedTo = (comment: AskRequestQueueComment, assignedToType: AssignedToType): AssignedTo => {
  const id = comment.user?.userId || comment.userLocation?.userId || comment.id;
  const name = comment.user?.name || comment.user?.username || comment.userLocation?.email;

  return {
    id,
    name,
    type: assignedToType,
  };
};

const buildAttachments = (comment: AskRequestQueueComment): Attachment[] | undefined => {
  if (!comment.attachments?.length) {
    return undefined;
  }

  return comment.attachments.map(attachment => ({
    filename: attachment.filename,
    attachmentId: attachment.attachmentId,
    fileType: attachment.fileType ?? extractFileType(attachment.filename),
  }));
};

export const buildMessagesCardProps = (
  comment: AskRequestQueueComment,
  options: BuildMessagesCardOptions = {}
): MessagesCardProps => {
  // Extract email metadata from comment.metadata.email if available
  const emailMetadata = options.emailMetadata ?? (comment as any).metadata?.email;
  const hasValidEmail = messagePreviewDetectors.isValidEmailMetadata(emailMetadata);
  const assignedToType = hasValidEmail ? 'ext-email' : deriveAssignedToType(comment);
  const assignedTo = buildAssignedTo(comment, assignedToType);

  const attachments = options.attachments ?? buildAttachments(comment);
  const createdAt = parseDateValue(comment.createdAt as string | Date | undefined);
  const dateTime = createdAt ? format(createdAt, 'PP p') : undefined;

  const taggedUserIds = parseTaggedUserIds(comment.taggedUserIds);
  const hasMentions =
    (comment.comment.match(/::[A-Za-z0-9]+::/g) && comment.comment.includes(`::${options.currentUserId}::`)) || false;
  const mentionTBD = options.currentUserId ? taggedUserIds.includes(options.currentUserId) : taggedUserIds.length > 0;

  const internalMessage = (comment.visibility ?? '').toLowerCase() === 'internal';
  const fallbackUser =
    options.fallbackUser ||
    (comment.user
      ? {
          userId: comment.user.userId ?? '',
          name: comment.user.name || comment.user.username || comment.user.userId || 'Unknown User',
        }
      : undefined);

  return {
    userId: options.currentUserId || '',
    assignedTo,
    mention: hasMentions,
    sentEmail: options.forceSentEmail ?? hasValidEmail,
    self: options.currentUserId ? comment.user?.userId === options.currentUserId : false,
    message: comment.comment ?? '',
    internalMessage,
    attachments,
    userName: options.isFirstMessage && emailMetadata?.from ? emailMetadata.from : assignedTo.name ?? 'Unknown user',
    userType: deriveUserTypeLabel(comment.userLocation?.role, assignedTo.type),
    dateTime,
    users: options.users ?? (comment.user ? [comment.user as User] : undefined),
    fallbackUser,
    skipMentionFormatting: options.skipMentionFormatting,
    checklist: options.checklist,
    details: options.details,
    isFirstMessage: options.isFirstMessage,
    emailMetadata,
    request: options.request,
    // Edit/Delete permissions from comment
    canEdit: comment.canUpdate,
    canDelete: comment.canDelete,
  };
};

/**
 * Helper to build MessagesCard props for a comment within an AskRequest context.
 * Automatically formats and includes request details for the first message.
 */
export const buildMessagesCardPropsWithRequest = (
  comment: AskRequestQueueComment,
  request: AskRequest,
  options: BuildMessagesCardOptions = {}
): MessagesCardProps => {
  // If this is the first message and details aren't already provided, format them from the request
  const details =
    options.isFirstMessage && !options.details
      ? formatAskRequestDetails(request, options.sectionAmount)
      : options.details;

  return buildMessagesCardProps(comment, {
    ...options,
    details,
    request,
  });
};
